/**
 * Skills Intelligence™ — client access to Layer 2/4/5.
 *
 * The browser never scores anything. It posts evidence to the
 * `skills-engine-compute` edge function, which runs the portable engine and
 * returns the sealed computation: inferred skills (mapped to ESCO) and the
 * five living Future Probability indicators, each with its explanation.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type {
  EvidenceRecord,
  InferredSkill,
  ProbabilityIndicator,
} from "@wcc360/engine/skills-engine";

export type StoredEvidence = Pick<EvidenceRecord, "item_id" | "option_ids" | "source"> & {
  note?: string | null;
  occurred_at?: string;
};

export interface SkillsState {
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** 0–1 share of the 40-item bank answered. */
  coverage: number;
  skills: InferredSkill[];
  indicators: ProbabilityIndicator[];
  evidence: StoredEvidence[];
  computedAt: string | null;
}

type Op = "read" | "save" | "compute";

async function callEngine(op: Op, payload: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("skills-engine-compute", {
    body: { op, ...payload },
  });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return data as {
    skills?: unknown[];
    indicators?: unknown[];
    evidence?: StoredEvidence[];
    coverage?: number;
    computed_at?: string;
  };
}

/** Rows come back from Postgres in snake_case; normalize to the engine shape. */
function toSkills(rows: unknown[] | undefined): InferredSkill[] {
  return (rows ?? []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      skill_id: String(row.skill_id),
      label: String(row.label),
      domain: row.domain as InferredSkill["domain"],
      level: row.level as InferredSkill["level"],
      level_score: Number(row.level_score ?? 0),
      confidence: Number(row.confidence ?? 0),
      evidence: Array.isArray(row.evidence) ? (row.evidence as string[]) : [],
      taxonomy: (row.taxonomy ?? {}) as InferredSkill["taxonomy"],
    };
  });
}

function toIndicators(rows: unknown[] | undefined): ProbabilityIndicator[] {
  return (rows ?? []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      key: (row.key ?? row.indicator) as ProbabilityIndicator["key"],
      label: String(row.label ?? ""),
      value: Number(row.value ?? 50),
      confidence: Number(row.confidence ?? 0),
      components: Array.isArray(row.components)
        ? (row.components as ProbabilityIndicator["components"])
        : [],
      explanation: String(row.explanation ?? ""),
    };
  });
}

export function useSkillsIntelligence() {
  const [state, setState] = useState<SkillsState>({
    loading: true,
    saving: false,
    error: null,
    coverage: 0,
    skills: [],
    indicators: [],
    evidence: [],
    computedAt: null,
  });

  const absorb = useCallback((data: Awaited<ReturnType<typeof callEngine>>, patch: Partial<SkillsState> = {}) => {
    setState((prev) => ({
      ...prev,
      loading: false,
      saving: false,
      error: null,
      coverage: typeof data.coverage === "number" ? data.coverage : prev.coverage,
      skills: data.skills ? toSkills(data.skills) : prev.skills,
      indicators: data.indicators ? toIndicators(data.indicators) : prev.indicators,
      evidence: data.evidence ?? prev.evidence,
      computedAt: data.computed_at ?? prev.computedAt,
      ...patch,
    }));
  }, []);

  const refresh = useCallback(async () => {
    setState((p) => ({ ...p, loading: true }));
    try {
      absorb(await callEngine("read"));
    } catch (e) {
      setState((p) => ({ ...p, loading: false, error: e instanceof Error ? e.message : "Could not load your skills" }));
    }
  }, [absorb]);

  /** Save answers and get the fresh computation back in the same round trip. */
  const saveEvidence = useCallback(async (records: StoredEvidence[]) => {
    setState((p) => ({ ...p, saving: true, error: null }));
    try {
      const data = await callEngine("save", { records });
      absorb(data);
      return true;
    } catch (e) {
      setState((p) => ({ ...p, saving: false, error: e instanceof Error ? e.message : "Could not save" }));
      return false;
    }
  }, [absorb]);

  /** Recompute from stored evidence + the latest living signals. */
  const recompute = useCallback(async () => {
    setState((p) => ({ ...p, saving: true, error: null }));
    try {
      absorb(await callEngine("compute"));
      return true;
    } catch (e) {
      setState((p) => ({ ...p, saving: false, error: e instanceof Error ? e.message : "Could not refresh" }));
      return false;
    }
  }, [absorb]);

  useEffect(() => { void refresh(); }, [refresh]);

  return { ...state, refresh, saveEvidence, recompute };
}
